import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured, useAuthStore } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Generate a random color for cursor
const CURSOR_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
  '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788',
  '#E63946', '#A8DADC', '#457B9D', '#E76F51', '#2A9D8F',
  '#F4A261', '#E9C46A', '#8338EC', '#FF006E', '#3A86FF'
];

const getRandomColor = (userId: string) => {
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return CURSOR_COLORS[hash % CURSOR_COLORS.length];
};

// Generate anonymous Pokemon-themed names
const POKEMON_ADJECTIVES = [
  'Brave', 'Swift', 'Mystic', 'Shiny', 'Shadow', 'Crystal',
  'Cosmic', 'Golden', 'Electric', 'Blazing', 'Frozen', 'Ancient'
];

const POKEMON_NAMES = [
  'Trainer', 'Champion', 'Explorer', 'Collector', 'Master',
  'Hunter', 'Seeker', 'Guardian', 'Wanderer', 'Scout'
];

const getRandomName = (id: string) => {
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const adj = POKEMON_ADJECTIVES[hash % POKEMON_ADJECTIVES.length];
  const name = POKEMON_NAMES[(hash * 7) % POKEMON_NAMES.length];
  return `${adj} ${name}`;
};

export interface CursorPosition {
  x: number;
  y: number;
  viewLevel: 'series' | 'sets' | 'cards';
  viewMode: 'galaxy' | 'timeline';
}

export interface UserPresence {
  odersId: string;
  odersNickname: string;
  color: string;
  cursor: CursorPosition;
  lastActive: number;
}

interface PresenceState {
  [key: string]: UserPresence[];
}

// Cursor update event for fast broadcast
interface CursorMoveEvent {
  id: string;
  x: number;
  y: number;
  viewLevel: 'series' | 'sets' | 'cards';
  viewMode: 'galaxy' | 'timeline';
}

export function usePokeverseCursors() {
  const [cursors, setCursors] = useState<Map<string, UserPresence>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const myIdRef = useRef<string>('');
  const myNicknameRef = useRef<string>('');
  const myColorRef = useRef<string>('');
  const lastBroadcastRef = useRef<number>(0);
  const userMetaRef = useRef<Map<string, { nickname: string; color: string }>>(new Map());
  
  const { user } = useAuthStore();

  // Initialize user identity
  useEffect(() => {
    if (user?.id) {
      myIdRef.current = user.id;
      myNicknameRef.current = user.user_metadata?.username || user.email?.split('@')[0] || getRandomName(user.id);
    } else {
      // Anonymous user - generate random ID
      const storedId = sessionStorage.getItem('pokeverse_cursor_id');
      if (storedId) {
        myIdRef.current = storedId;
      } else {
        myIdRef.current = `anon_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('pokeverse_cursor_id', myIdRef.current);
      }
      myNicknameRef.current = getRandomName(myIdRef.current);
    }
    myColorRef.current = getRandomColor(myIdRef.current);
  }, [user]);

  // Set up presence channel with fast broadcast
  useEffect(() => {
    if (!isSupabaseConfigured()) return;

    const channel = supabase.channel('pokeverse-cursors', {
      config: {
        broadcast: {
          self: false, // Don't receive own broadcasts
        },
        presence: {
          key: myIdRef.current,
        },
      },
    });

    channelRef.current = channel;

    // Handle fast cursor broadcasts (for real-time movement)
    channel.on('broadcast', { event: 'cursor_move' }, ({ payload }: { payload: CursorMoveEvent }) => {
      if (payload.id === myIdRef.current) return;
      
      setCursors(prev => {
        const existing = prev.get(payload.id);
        if (!existing) return prev; // User not in presence yet
        
        const updated = new Map(prev);
        updated.set(payload.id, {
          ...existing,
          cursor: {
            x: payload.x,
            y: payload.y,
            viewLevel: payload.viewLevel,
            viewMode: payload.viewMode,
          },
          lastActive: Date.now(),
        });
        return updated;
      });
    });

    // Handle presence sync (for user join/leave and initial state)
    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState() as PresenceState;
      const newCursors = new Map<string, UserPresence>();
      
      Object.entries(state).forEach(([key, presences]) => {
        if (presences.length > 0) {
          const presence = presences[0];
          // Don't show our own cursor
          if (presence.odersId !== myIdRef.current) {
            newCursors.set(presence.odersId, presence);
            // Cache user metadata for fast broadcasts
            userMetaRef.current.set(presence.odersId, {
              nickname: presence.odersNickname,
              color: presence.color,
            });
          }
        }
      });
      
      setCursors(newCursors);
    });

    // Handle user join
    channel.on('presence', { event: 'join' }, ({ newPresences }) => {
      if (newPresences.length > 0) {
        const presence = newPresences[0] as UserPresence;
        if (presence.odersId !== myIdRef.current) {
          userMetaRef.current.set(presence.odersId, {
            nickname: presence.odersNickname,
            color: presence.color,
          });
          setCursors(prev => {
            const updated = new Map(prev);
            updated.set(presence.odersId, presence);
            return updated;
          });
        }
      }
    });

    // Handle user leave
    channel.on('presence', { event: 'leave' }, ({ leftPresences }) => {
      if (leftPresences.length > 0) {
        const presence = leftPresences[0] as UserPresence;
        userMetaRef.current.delete(presence.odersId);
        setCursors(prev => {
          const updated = new Map(prev);
          updated.delete(presence.odersId);
          return updated;
        });
      }
    });

    // Subscribe to channel
    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        setIsConnected(true);
        // Track our presence (for join/leave events)
        await channel.track({
          odersId: myIdRef.current,
          odersNickname: myNicknameRef.current,
          color: myColorRef.current,
          cursor: { x: 0, y: 0, viewLevel: 'series', viewMode: 'galaxy' },
          lastActive: Date.now(),
        });
      }
    });

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
      setIsConnected(false);
    };
  }, []);

  // Update cursor position with minimal throttling using fast broadcast
  const updateCursor = useCallback((position: CursorPosition) => {
    if (!channelRef.current || !isConnected) return;

    // Throttle to ~60fps for smooth movement
    const now = Date.now();
    if (now - lastBroadcastRef.current < 16) return;
    lastBroadcastRef.current = now;

    // Use fast broadcast for cursor movement (lower latency than presence.track)
    channelRef.current.send({
      type: 'broadcast',
      event: 'cursor_move',
      payload: {
        id: myIdRef.current,
        x: position.x,
        y: position.y,
        viewLevel: position.viewLevel,
        viewMode: position.viewMode,
      } as CursorMoveEvent,
    });
  }, [isConnected]);

  return {
    cursors: Array.from(cursors.values()),
    updateCursor,
    isConnected,
    myId: myIdRef.current,
    activeUsers: cursors.size + 1, // Real users + self
  };
}
